// QR部分消費モデルの共通ヘルパー。
// 数量の正規単位は「割合」(used_ratio、1.0 = 1袋/1セット全量)。QRの残量は保存せず常に Σ used_ratio から導出する。
// kg は 割合 × MaterialDetail.content_quantity(kg単位細目の合計 = セットkg)で都度換算する(材料受払簿と同式)。

// 「ほぼ全量」判定の閾値。Σ used_ratio ≥ 1 - FULL_EPS で全量消費とみなす(api 側 utils/consumptionCache.ts と同値)。
export const FULL_EPS = 1e-3;

type ConsumptionLike = {
  material_qr_id: number;
  used_ratio: number;
  deleted_at: number | null;
};

type MaterialDetailLike = {
  material_id: number | null;
  content_quantity: number | null;
  content_unit: string;
  deleted_at: number | null;
};

type DeletedFlagRow = {
  id: string;
  deleted_at: number | null;
};

type ConsumptionParentLike = {
  construction_id: string;
  stir_batch_id: string | null;
  process_execution_id: string | null;
};

type ConsumptionParentsLike = {
  constructions: DeletedFlagRow[];
  stirBatches: DeletedFlagRow[];
  processExecutions: DeletedFlagRow[];
};

function deletedParentLabel(id: string | null, rows: DeletedFlagRow[], label: string): string | null {
  if (id == null) {
    return null;
  }
  const parent = rows.find((row) => row.id === id);
  if (parent == null) {
    return null;
  }
  if (parent.deleted_at != null) {
    return label;
  }
  return null;
}

// 消費行が参照する削除済み親を、施工 → バッチ → 工程の優先順で1件だけ返す。
// 親行が未取得の場合は同期不足の可能性があるため、削除済み扱いにしない。
export function consumptionParentDeletedLabel(consumption: ConsumptionParentLike, parents: ConsumptionParentsLike): string | null {
  const constructionLabel = deletedParentLabel(consumption.construction_id, parents.constructions, "施工が削除済み");
  if (constructionLabel != null) {
    return constructionLabel;
  }
  const batchLabel = deletedParentLabel(consumption.stir_batch_id, parents.stirBatches, "バッチが削除済み");
  if (batchLabel != null) {
    return batchLabel;
  }
  const processLabel = deletedParentLabel(consumption.process_execution_id, parents.processExecutions, "工程が削除済み");
  if (processLabel != null) {
    return processLabel;
  }
  return null;
}

type StirBatchParentLike = DeletedFlagRow & { construction_id: string; process_execution_id: string | null };
type ProcessExecutionParentLike = DeletedFlagRow & { construction_id: string };

type ConsumptionWritePayload = {
  construction_id: string;
  material_qr_id: number;
  stir_batch_id: string | null;
  process_execution_id: string | null;
  used_ratio: number;
  // patch/復元で自行を Σ から除外するための消費行id。新規作成(put)は null。
  excludeConsumptionId: string | null;
};

type ConsumptionWriteRows = {
  constructions: DeletedFlagRow[];
  stirBatches: StirBatchParentLike[];
  processExecutions: ProcessExecutionParentLike[];
  consumptions: (ConsumptionLike & { id: string })[];
};

// オフライン書き込み(put/patch/復元)前の消費行検証。API 側(assertConsumptionParentsAlive /
// assertQrTotalWithinOne)と同じ意味論をローカル IndexedDB のデータで再現し、違反があれば
// 日本語のエラーメッセージを返す(問題なければ null)。オフラインで通した書き込みが復帰マージで
// サーバに拒否されない状態を、書き込み前にローカルで保証するための防御。
// 親行が IndexedDB に見つからない場合は同期不足の可能性があるため拒否しない(エラーチップの判定と
// 同じ「削除済みと確認できたときだけ拒否する」規則。オフラインで確認できない書き込みはサーバ側の
// 検証・merge に委ねる)。
// checkParents=false は patch の親参照が実変更されない場合用(API と同じく、座礁行の残量修正を
// 親検証で塞がない)。Σ ≤ 1 の検証は常に行う。
export function validateConsumptionWrite(payload: ConsumptionWritePayload, rows: ConsumptionWriteRows, checkParents: boolean = true): string | null {
  if (checkParents) {
    const parentError = validateConsumptionParents(payload, rows);
    if (parentError != null) {
      return parentError;
    }
  }

  let total = payload.used_ratio;
  for (const row of rows.consumptions) {
    if (row.material_qr_id === payload.material_qr_id && row.deleted_at == null && row.id !== payload.excludeConsumptionId) {
      total += row.used_ratio ?? 1;
    }
  }
  if (total > 1 + FULL_EPS) {
    return `材料QR ${payload.material_qr_id} の消費合計が1袋を超えます(合計 ${total.toFixed(3)})。画面を更新して残量を確認してください。`;
  }

  return null;
}

// 親(施工/撹拌バッチ/工程実施)の生存と所属整合の検証本体。API 側 assertConsumptionParentsAlive と
// 同じ規則だが、IndexedDB に親行が見つからない場合は同期不足として拒否しない(見つかった行のみ検証)。
function validateConsumptionParents(payload: ConsumptionWritePayload, rows: ConsumptionWriteRows): string | null {
  const construction = rows.constructions.find((row) => row.id === payload.construction_id);
  if (construction != null && construction.deleted_at != null) {
    return "付け替え先の施工が削除済みまたは存在しません。画面を更新して最新の状態を確認してください。";
  }

  if (payload.stir_batch_id != null) {
    const stirBatch = rows.stirBatches.find((row) => row.id === payload.stir_batch_id);
    if (stirBatch != null) {
      if (stirBatch.deleted_at != null) {
        return "付け替え先の撹拌バッチが削除済みまたは存在しません。画面を更新して最新の状態を確認してください。";
      }
      if (stirBatch.construction_id !== payload.construction_id) {
        return "指定の撹拌バッチはこの施工に属していません。画面を更新して最新の状態を確認してください。";
      }
      // 明示的な null 指定も「バッチの工程と食い違う」として拒否する(API と同じ規則)。
      if (stirBatch.process_execution_id != null && stirBatch.process_execution_id !== payload.process_execution_id) {
        return "指定の工程実施が撹拌バッチの工程実施と一致しません。画面を更新して最新の状態を確認してください。";
      }
    }
  }

  if (payload.process_execution_id != null) {
    const processExecution = rows.processExecutions.find((row) => row.id === payload.process_execution_id);
    if (processExecution != null) {
      if (processExecution.deleted_at != null) {
        return "付け替え先の工程実施が削除済みまたは存在しません。画面を更新して最新の状態を確認してください。";
      }
      if (processExecution.construction_id !== payload.construction_id) {
        return "指定の工程実施はこの施工に属していません。画面を更新して最新の状態を確認してください。";
      }
    }
  }

  return null;
}

// QRの累計消費割合(0..1 に clamp)。論理削除行は除外。
// 旧キャッシュ行(used_ratio 未保持)は全量消費(1.0)として扱う(部分消費導入前の行は常に1袋消費のため)。
export function getQrConsumedRatio(cmc: ConsumptionLike[], qrId: number): number {
  let total = 0;
  for (const row of cmc) {
    if (row.material_qr_id === qrId && row.deleted_at == null) {
      total += row.used_ratio ?? 1;
    }
  }
  return Math.min(1, Math.max(0, total));
}

// QRの残割合 = max(0, 1 - Σ)。Σ ≥ 1 - FULL_EPS(全量消費とみなす閾値)は 0 を返す。
// そのまま 1 - Σ を返すと、丸め誤差起因の 0.001 袋程度の端数残が表示されてしまうため。
export function getQrRemainingRatio(cmc: ConsumptionLike[], qrId: number): number {
  const consumed = getQrConsumedRatio(cmc, qrId);
  if (consumed >= 1 - FULL_EPS) {
    return 0;
  }
  return Math.max(0, 1 - consumed);
}

// 全量消費済みか(Σ ≥ 1 - FULL_EPS)。
export function isQrFullyConsumed(cmc: ConsumptionLike[], qrId: number): boolean {
  return getQrConsumedRatio(cmc, qrId) >= 1 - FULL_EPS;
}

// 一部消費(残量あり)か(0 < Σ < 1 - FULL_EPS)。
export function isQrPartiallyConsumed(cmc: ConsumptionLike[], qrId: number): boolean {
  const ratio = getQrConsumedRatio(cmc, qrId);
  return ratio > 0 && ratio < 1 - FULL_EPS;
}

// 材料の1セットkg = content_unit が 'kg' の細目の content_quantity 合計(受払簿 sumContentKg と同式)。
export function getMaterialSetKg(materialDetails: MaterialDetailLike[], materialId: number): number {
  let total = 0;
  for (const d of materialDetails) {
    const isTargetKgDetail = d.material_id === materialId && d.deleted_at == null && d.content_unit === "kg";
    if (isTargetKgDetail) {
      total += d.content_quantity ?? 0;
    }
  }
  return total;
}

// 割合を「10.0kg (0.50袋)」表記にする。セットkg不明(0)時は袋数のみ。
export function formatRatioAsBagsKg(ratio: number, setKg: number): string {
  const bags = ratio.toFixed(2);
  if (setKg <= 0) {
    return `${bags}袋`;
  }
  const kg = (ratio * setKg).toFixed(1);
  return `${kg}kg (${bags}袋)`;
}

// 割合を「0.50袋 (3.4kg)」表記にする(残量表示用。袋を主、kg換算を括弧で併記)。セットkg不明(0)時は袋のみ。
// ※単位に材料マスタの standard_usage_unit は使わない(kg/m² 等の施工量あたりレート単位で、残量の数量単位として意味が合わないため)。
export function formatRatioAsBagsThenKg(ratio: number, setKg: number): string {
  const bags = ratio.toFixed(2);
  if (setKg <= 0) {
    return `${bags}袋`;
  }
  return `${bags}袋 (${(ratio * setKg).toFixed(1)}kg)`;
}
